import axios from '../helpers/axios'
import { logError } from '../services';
import {
  IBuildDocumentationDetails,
  IBuildDocumentationResponse,
  IDropdownResponse,
  IBuildDocument,
  IBuildDocumentTask,
  IDocumentationTaskFile
} from '../models';

const baseUrl = '/api/documentation';

export const getBuildDocumentation = async (filters?: {
  buildStateFilter: string;
  neighborhoodId: string | number;
  roleFilter?: string;
  page?: number;
  perPage?: number | string;
  sortBy?: string;
  sortDirection?: string;
  excludeHiddenBuilds?: boolean;
  closingDateLocked?: string;
  termiteLetterUploaded?: string;
  coUploaded?: string;
  searchText?: string;
}): Promise<IBuildDocumentationResponse> => {
  try {
    const { data } = await axios.get(`${baseUrl}/builds`, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getBuildDocumentation failed');
    return Promise.reject(error);
  }
};

export const getBuildDocumentationDetails = async (
  buildId: string | number
): Promise<IBuildDocumentationDetails> => {
  try {
    const { data } = await axios.get(`${baseUrl}/builds/${buildId}`);
    return data;
  } catch (error) {
    logError(error, 'getBuildDocumentationDetails failed');
    return Promise.reject(error);
  }
};

export const updateBuildDocumentationDetailsLender = async (
  buildId: string | number,
  lenderId: string | number
): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.put(`${baseUrl}/builds/${buildId}/lender`, lenderId, {
      headers: {
        'Content-Type': 'application/json-patch+json'
      }
    });

    return data;
  } catch (error) {
    logError(error, 'updateBuildDocumentationDetailsLender failed');
    return Promise.reject(error);
  }
};

export const getBuildDocumentationLenders = async (
  neighborhoodId: string | number
): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`${baseUrl}/lenders/${neighborhoodId}`);
    return data;
  } catch (error) {
    logError(error, 'getBuildDocumentationLenders failed');
    return Promise.reject(error);
  }
};

export const getDocumentationBuildStatuses = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`${baseUrl}/build-statuses`);
    return data;
  } catch (error: any) {
    logError(error, 'getDocumentationBuildStatuses failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getDocumentationNeighborhoods = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`${baseUrl}/neighborhoods`);
    return data;
  } catch (error: any) {
    logError(error, 'getDocumentationNeighborhoods failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getDocumentationRoles = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`${baseUrl}/roles`);
    return data;
  } catch (error: any) {
    logError(error, 'getDocumentationRoles failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getBuildDocuments = async (buildId: number | string): Promise<IBuildDocument[]> => {
  try {
    const { data } = await axios.get(`${baseUrl}/builds/${buildId}/documents`);
    return data;
  } catch (error: any) {
    logError(error, 'getBuildDocuments failed');
    return Promise.reject(error.response.data);
  }
};

export const downloadBuildDocument = async (buildDocumentId: number | string): Promise<any> => {
  try {
    const data = await axios.get(`${baseUrl}/documents/download/${buildDocumentId}`, {
      responseType: 'blob'
    });
    return data;
  } catch (error: any) {
    logError(error, 'downloadBuildDocument failed');
    return Promise.reject(error.response.data);
  }
};

export const regenerateClosingDocuments = async (buildId: number | string): Promise<any> => {
  try {
    const { data } = await axios.post(
      `${baseUrl}/builds/${buildId}/admin/regenerate-closing-documents`
    );
    return data;
  } catch (error: any) {
    logError(error, 'regenerateClosingDocuments failed');
    return Promise.reject(error.response.data);
  }
};

export const regenerateOptionsReport = async (buildId: number | string): Promise<any> => {
  try {
    const { data } = await axios.post(
      `${baseUrl}/builds/${buildId}/admin/regenerate-options-report`
    );
    return data;
  } catch (error: any) {
    logError(error, 'regenerateOptionsReport failed');
    return Promise.reject(error.response.data);
  }
};

export const getBuildDocumentTasks = async (
  buildId: number | string
): Promise<IBuildDocumentTask[]> => {
  try {
    const { data } = await axios.get(`${baseUrl}/builds/${buildId}/tasks`);
    return data;
  } catch (error: any) {
    logError(error, 'getBuildDocumentTasks failed');
    return Promise.reject(error.response.data);
  }
};

export const uploadTaskBuildDocument = async (
  buildId: number | string,
  buildDocumentFile: any,
  documentationTaskId: number,
  documentTaskName: string
): Promise<any> => {
  try {
    const { data } = await axios.post(
      `${baseUrl}/builds/${buildId}/tasks/${documentationTaskId}/upload?DocumentTaskName=${documentTaskName}`,
      buildDocumentFile
    );
    return data;
  } catch (error: any) {
    logError({ error, payload: { buildId, buildDocumentFile } }, 'uploadTaskBuildDocument failed');
    return Promise.reject(error?.response?.data);
  }
};

export const uploadMultipleTaskBuildDocuments = async (
  buildId: number | string,
  buildDocumentFile: any,
  documentationTaskId: number,
  documentTaskName: string
): Promise<any> => {
  try {
    const { data } = await axios.post(
      `${baseUrl}/builds/${buildId}/tasks/${documentationTaskId}/upload-multiple?DocumentTaskName=${documentTaskName}`,
      buildDocumentFile
    );
    return data;
  } catch (error) {
    logError(
      { error, payload: { buildId, buildDocumentFile } },
      'uploadMultipleTaskBuildDocuments failed'
    );
    return Promise.reject(error?.response?.data);
  }
};

export const getDocumentationTaskFiles = async (
  buildId: number,
  documentationTaskId: number
): Promise<IDocumentationTaskFile[]> => {
  try {
    const { data } = await axios.get(
      `${baseUrl}/builds/${buildId}/tasks/${documentationTaskId}/files`
    );
    return data;
  } catch (error: any) {
    logError(error, 'getDocumentationTaskFiles failed');
    return Promise.reject(error.response.data);
  }
};

export const hideDocumentationFiles = async (
  payload: { buildDocumentId: number; hideInDocumentationPortal: boolean }[]
): Promise<any> => {
  try {
    const { data } = await axios.post(`${baseUrl}/documents/hide`, payload);
    return data;
  } catch (error: any) {
    logError(error, 'hideDocumentationFiles failed');
    return Promise.reject(error.response.data);
  }
};

export const updateBuildDocumentationDetailsFinanceType = async (
  buildId: string | number,
  financeType: string
): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.put(`${baseUrl}/builds/${buildId}/financing`, `"${financeType}"`, {
      headers: {
        'Content-Type': 'application/json-patch+json'
      }
    });
    return data;
  } catch (error) {
    logError(error, 'updateBuildDocumentationDetailsFinanceTyoe failed');
    return Promise.reject(error);
  }
};
